import type { PendingTopicData } from "../components/autopilot/TopicCard";

/** Brand kit assets as stored on the kit; only ready images can be placed. */
export interface ProposalBrandAsset {
  id: string;
  type?: string;
  storagePath: string;
  thumbnailPath?: string;
  processingStatus?: string;
}

export interface ProposalSlide {
  heading: string;
  /** Null means generation fills the slot (AI image, or text only). */
  image: { storagePath: string; source: "brand" | "article" } | null;
}

export interface PostProposal {
  topic: PendingTopicData;
  slides: ProposalSlide[];
  /** Slides that use the business's own photos (brand kit or website). */
  ownPhotoCount: number;
  articleImagePath?: string;
}

type Assignment = { id?: string } | null | undefined;

function resolveImage(
  assignment: Assignment,
  topic: PendingTopicData,
  assets: ProposalBrandAsset[],
): ProposalSlide["image"] {
  const id = assignment?.id;
  if (typeof id !== "string") return null;
  if (id.startsWith("ARTICLE_")) {
    const index = Number(id.slice("ARTICLE_".length));
    const path = topic.extractedArticleImages?.[index]?.storagePath;
    return path ? { storagePath: path, source: "article" } : null;
  }
  if (id.startsWith("BRANDKIT_")) {
    const ready = assets.filter(
      (asset) =>
        (asset.type ?? "image") === "image" &&
        asset.processingStatus === "ready" &&
        asset.storagePath,
    );
    // Mirrors findBrandKitImageIndex on the server: stable ids, then legacy positions.
    const legacy = /^BRANDKIT_(\d+)$/.exec(id);
    const asset =
      ready.find((candidate) => `BRANDKIT_${candidate.id}` === id) ||
      (legacy ? ready[Number(legacy[1])] : undefined);
    return asset
      ? {
          storagePath: asset.thumbnailPath || asset.storagePath,
          source: "brand",
        }
      : null;
  }
  return null;
}

/** The slides a proposal will become, with the images already chosen for them. */
export function proposalSlides(
  topic: PendingTopicData,
  assets: ProposalBrandAsset[],
): ProposalSlide[] {
  const { content } = topic;
  if (content.category === "quote") {
    return [
      {
        heading: content.content?.quote || "",
        image: resolveImage(content.assignedImage, topic, assets),
      },
    ];
  }
  const items: { title?: string; assignedImage?: Assignment }[] =
    content.content?.items || [];
  return [
    {
      heading: content.content?.title || "",
      image: resolveImage(content.titleSlideImage, topic, assets),
    },
    ...items.map((item) => ({
      heading: item.title || "",
      image: resolveImage(item.assignedImage, topic, assets),
    })),
  ];
}

const STOP_WORDS = new Set(
  "a an and at for from in into of on or the to with your our you who whats what how why top best".split(
    " ",
  ),
);

function titleTokens(title: string): Set<string> {
  return new Set(
    title
      .toLowerCase()
      .replace(/[’']/g, "")
      .split(/[^a-z0-9]+/)
      .filter((word) => word && !/^\d+$/.test(word) && !STOP_WORDS.has(word)),
  );
}

function similar(a: Set<string>, b: Set<string>) {
  let shared = 0;
  a.forEach((token) => {
    if (b.has(token)) shared++;
  });
  const union = a.size + b.size - shared;
  return union > 0 && shared / union >= 0.5;
}

// Months are 0-based. A seasonal idea outside its window is shown last.
const SEASONS: { pattern: RegExp; months: number[] }[] = [
  { pattern: /\bnew year|resolution/i, months: [11, 0] },
  { pattern: /christmas|holiday gift|\bholidays?\b/i, months: [10, 11] },
  { pattern: /thanksgiving/i, months: [9, 10] },
  { pattern: /halloween/i, months: [8, 9] },
  { pattern: /valentine/i, months: [0, 1] },
  { pattern: /easter/i, months: [2, 3] },
  { pattern: /fourth of july|4th of july|independence day/i, months: [5, 6] },
  { pattern: /\bsummer\b/i, months: [4, 5, 6, 7] },
  { pattern: /\bwinter\b/i, months: [10, 11, 0, 1] },
  { pattern: /\bspring\b/i, months: [1, 2, 3, 4] },
  // "fall" only as the season, not the verb ("Don't fall for these myths").
  {
    pattern: /\b(autumn|harvest|this fall|fall (season|colou?rs?|foliage|menu|wines?|getaway))\b/i,
    months: [7, 8, 9, 10],
  },
];

function outOfSeason(text: string, month: number) {
  return SEASONS.some(
    ({ pattern, months }) => pattern.test(text) && !months.includes(month),
  );
}

function proposalTitle(topic: PendingTopicData) {
  return topic.content.category === "quote"
    ? topic.content.content?.quote || ""
    : topic.content.content?.title || "";
}

const DAY = 24 * 60 * 60 * 1000;

function scoreIdea(
  topic: PendingTopicData,
  assets: ProposalBrandAsset[],
  now: number,
) {
  const slides = proposalSlides(topic, assets);
  const brand = slides.filter((s) => s.image?.source === "brand").length;
  const article = slides.filter((s) => s.image?.source === "article").length;
  const photoShare = slides.length
    ? (brand + article * 0.5) / slides.length
    : 0;
  const published = topic.articlePublishedAt || topic.discoveredAt || 0;
  const ageDays = Math.max(0, (now - published) / DAY);
  const recency = Math.max(0, 1 - ageDays / 730);
  const title = proposalTitle(topic);
  const seasonal = outOfSeason(
    `${title} ${topic.articleTitle || ""}`,
    new Date(now).getMonth(),
  );
  return {
    proposal: {
      topic,
      slides,
      ownPhotoCount: brand + article,
      articleImagePath: topic.extractedArticleImages?.find(
        (image) => image.storagePath,
      )?.storagePath,
    } as PostProposal,
    tokens: titleTokens(title),
    seasonal,
    // A carousel filled with their photos makes the strongest post; a
    // one-slide quote with one photo should not outrank it.
    score:
      photoShare * 2 +
      Math.min(brand + article, 4) * 0.25 +
      recency -
      (topic.content.category === "quote" ? 1 : 0) -
      (seasonal ? 10 : 0),
  };
}

type ScoredIdea = ReturnType<typeof scoreIdea>;

function byScore(a: ScoredIdea, b: ScoredIdea) {
  return (
    b.score - a.score || a.proposal.topic.topicIndex - b.proposal.topic.topicIndex
  );
}

/** Best ideas first, at most one per article and no near-duplicate titles. */
function distinct(scored: ScoredIdea[], limit = Infinity) {
  const kept: ScoredIdea[] = [];
  const articles = new Set<string>();
  for (const entry of scored) {
    const article = entry.proposal.topic.articleUrl;
    if (article && articles.has(article)) continue;
    if (kept.some((other) => similar(other.tokens, entry.tokens))) continue;
    if (article) articles.add(article);
    kept.push(entry);
    if (kept.length >= limit) break;
  }
  return kept;
}

/**
 * Orders pending ideas for a first-post choice: carousels that use the
 * business's own photos and recent articles first, one idea per article,
 * near-duplicate titles removed, and out-of-season ideas last (or left out
 * when there are enough others).
 */
export function rankProposals(
  topics: PendingTopicData[],
  assets: ProposalBrandAsset[],
  { now = Date.now(), limit = 6 }: { now?: number; limit?: number } = {},
): PostProposal[] {
  const scored = topics
    .filter((topic) => topic.status === "pending")
    .map((topic) => scoreIdea(topic, assets, now))
    .sort(byScore);
  // Only fall back to out-of-season ideas when there is little else to show.
  const inSeason = scored.filter((entry) => !entry.seasonal);
  const candidates = inSeason.length >= 3 ? inSeason : scored;
  return distinct(candidates, limit).map((entry) => entry.proposal);
}
